import { useState, useRef, useEffect } from 'react'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Form from 'react-bootstrap/Form'
import Button from 'react-bootstrap/Button'
import Alert from 'react-bootstrap/Alert'
import Badge from 'react-bootstrap/Badge'
import Spinner from 'react-bootstrap/Spinner'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus } from '@fortawesome/free-solid-svg-icons'
import { useCreatePolicyDocument } from 'api/hooks/use-policy-documents'
import useSections from 'api/hooks/use-sections'

const PolicyCreateForm = ({ onCreated }) => {
  const [name, setName] = useState('')
  const [selectedSections, setSelectedSections] = useState([])
  const [showSectionPicker, setShowSectionPicker] = useState(false)
  const [error, setError] = useState(null)
  const pickerRef = useRef(null)

  const { data: sectionsData } = useSections()
  const sections = (sectionsData || [])
    .map((s) => ({ id: s.id, name: s.name }))
    .sort((a, b) => a.name.localeCompare(b.name))

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) {
        setShowSectionPicker(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const { mutate: create, isLoading } = useCreatePolicyDocument({
    options: {
      onSuccess: (data) => {
        setName('')
        setSelectedSections([])
        setError(null)
        if (onCreated) onCreated(data.policy_document?.id)
      },
      onError: (err) => {
        setError(err.response?.data?.message || 'Failed to create policy document.')
      }
    }
  })

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!name.trim() || isLoading) return
    setError(null)
    create({ name: name.trim(), sections: selectedSections })
  }

  const toggleSection = (sectionId) => {
    setSelectedSections((prev) =>
      prev.includes(sectionId) ? prev.filter((s) => s !== sectionId) : [...prev, sectionId]
    )
  }

  return (
    <>
      <Form onSubmit={handleSubmit}>
        <Row className="align-items-end g-2">
          <Col sm={5}>
            <Form.Group>
              <Form.Label className="small fw-bold">Policy Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g. Purchasing Policy 2025"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={isLoading}
                size="sm"
              />
            </Form.Group>
          </Col>
          <Col sm={5}>
            <Form.Group>
              <Form.Label className="small fw-bold">Sections</Form.Label>
              <div className="position-relative" ref={pickerRef}>
                <div
                  className="form-control form-control-sm d-flex flex-wrap gap-1 align-items-center"
                  style={{ minHeight: '31px', cursor: 'pointer' }}
                  onClick={() => setShowSectionPicker((v) => !v)}
                >
                  {selectedSections.length === 0 ? (
                    <span className="text-muted small">Select sections...</span>
                  ) : (
                    selectedSections.map((id) => {
                      const section = sections.find((s) => s.id === id)
                      return (
                        <Badge key={id} className="badge-secondary" bg="" style={{ fontWeight: 'normal' }}>
                          {section?.name}
                        </Badge>
                      )
                    })
                  )}
                </div>
                {showSectionPicker && sections.length > 0 && (
                  <div
                    className="position-absolute bg-white border rounded shadow-sm"
                    style={{ zIndex: 20, minWidth: '200px', maxHeight: '200px', overflowY: 'auto', top: '100%', left: 0 }}
                  >
                    {sections.map((s) => (
                      <label
                        key={s.id}
                        className="d-flex align-items-center px-2 py-1 small gap-2"
                        style={{ cursor: 'pointer', whiteSpace: 'nowrap', margin: 0 }}
                      >
                        <input
                          type="checkbox"
                          checked={selectedSections.includes(s.id)}
                          onChange={() => toggleSection(s.id)}
                          style={{ cursor: 'pointer' }}
                        />
                        {s.name}
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </Form.Group>
          </Col>
          <Col sm={2}>
            <Button
              type="submit"
              variant="primary"
              disabled={isLoading || !name.trim()}
              size="sm"
              className="w-100"
            >
              {isLoading ? (
                <Spinner as="span" animation="border" size="sm" />
              ) : (
                <>
                  <FontAwesomeIcon icon={faPlus} className="me-1" />
                  Add Policy
                </>
              )}
            </Button>
          </Col>
        </Row>
      </Form>
      {error && (
        <Alert variant="danger" className="mt-2 py-2" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
    </>
  )
}

export default PolicyCreateForm
